import React from 'react';
import { Icon, message, Tooltip } from 'antd';
import { getTheme } from '~/common/utils/theme';
import { TicketBatchOperation } from './enums';

type BatchPayload = {
  ids?: Array<number | string>;
  ticketsId?: Array<number | string>;
  operation?: TicketBatchOperation;
};

type BatchResultItem = {
  ticketId: number | string;
  success: boolean;
};

type BatchResponse = {
  results?: BatchResultItem[];
  ids?: Array<number | string>;
};

type CopyButtonProps = {
  text: string;
  color: string;
};

const copyToClipboard = (text: string) => {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

function CopyButton({ text, color }: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    copyToClipboard(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 3000);
  };

  return React.createElement(
    Tooltip,
    { title: copied ? 'Copiado!' : 'Copiar' },
    React.createElement(
      'button',
      {
        type: 'button',
        onClick: handleCopy,
        style: {
          border: 'none',
          background: 'transparent',
          padding: 0,
          marginLeft: 8,
          marginBottom: 6,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
        },
        title: 'Copiar',
      },
      React.createElement(Icon, { type: copied ? 'check' : 'copy', style: { color } }),
    ),
  );
}

type RenderMessageContentParams = {
  text: string;
  copyable?: boolean;
  forceFlexParent?: boolean;
};

const renderMessageContent = ({ text, copyable = false, forceFlexParent = false }: RenderMessageContentParams) => {
  let contentRef: ((node: HTMLSpanElement | null) => void) | undefined;

  if (forceFlexParent) {
    contentRef = (node: HTMLSpanElement | null) => {
      if (!node) return;
      const successParent = node.closest('.ant-message-custom-content.ant-message-success') as HTMLElement | null;
      const errorParent = node.closest('.ant-message-custom-content.ant-message-error') as HTMLElement | null;
      if (errorParent) {
        errorParent.style.display = 'flex';
      }
      if (successParent) {
        successParent.style.display = 'flex';
      }
    };
  }

  let copyButton: React.ReactNode = null;

  if (copyable) {
    copyButton = React.createElement(CopyButton, { text, color: getBrandPrimary() || '#D80073' });
  }

  return React.createElement(
    'span',
    {
      ref: contentRef,
      style: {
        display: 'inline-block',
        maxWidth: 1000,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        verticalAlign: 'middle',
      },
    },
    text,
    copyButton,
  );
};

const getBrandPrimary = () => {
  const theme = getTheme(localStorage.getItem('theme'));
  return theme?.colors?.brand?.primary;
};

export const showBatchUpdateMessages = (data: BatchResponse, payload: BatchPayload) => {
  const requestedIds = payload?.ids ?? payload?.ticketsId ?? [];
  const results = data?.results ?? [];
  const hasResults = results.length > 0;

  let successIds: Array<number | string> = [];

  if (hasResults) {
    successIds = results.filter((item) => item?.success).map((item) => item.ticketId);
  } else {
    successIds = data?.ids ?? [];
  }

  let failedIds: Array<number | string> = [];

  if (hasResults) {
    failedIds = results.filter((item) => item?.success === false).map((item) => item.ticketId);
  } else {
    failedIds = requestedIds.filter((id) => !successIds.includes(id));
  }

  let successMessage = 'Responsável alterado com sucesso';
  if (payload.operation === TicketBatchOperation.RESOLVING_AREA) {
    successMessage = 'Área resolvedora alterada com sucesso';
  }

  const errorMessage = `Erro ao alterar área resolvedora aos tickets ${failedIds.join(', ')}.`;

  if (!failedIds.length && successIds.length) {
    message.success({ content: renderMessageContent({ text: successMessage }), duration: 6 });
    return;
  }

  if (successIds.length) {
    message.open({
      type: 'success',
      content: renderMessageContent({
        text: `Tickets ${successIds.join(', ')} atualizados com sucesso.`,
        copyable: true,
        forceFlexParent: true,
      }),
      duration: 6,
    });
  }

  if (failedIds.length) {
    message.open({
      type: 'error',
      content: renderMessageContent({ text: errorMessage, copyable: true, forceFlexParent: true }),
      duration: 6,
    });
  }
};
